import { computed, readonly, ref } from 'vue'
import {
  getCurrentEducationWorkspace,
  type CurrentEducationWorkspaceVO,
  type EducationOrganizationOptionVO
} from '@/api/aiEducation'
import { getCurrentUserId, getTenantId } from '@/utils/auth'
import { setEducationMembershipId } from './educationMembershipContext'

type EducationWorkspaceStatus = 'idle' | 'loading' | 'ready' | 'error'

const STORAGE_PREFIX = 'ai-education:membership'
const workspace = ref<CurrentEducationWorkspaceVO>()
const status = ref<EducationWorkspaceStatus>('idle')
const error = ref<unknown>()
let pendingInitialization: Promise<CurrentEducationWorkspaceVO> | undefined

const storageKey = (userId = getCurrentUserId(), tenantId = Number(getTenantId()) || 0) =>
  `${STORAGE_PREFIX}:${tenantId}:${userId}`

const readStoredMembershipId = () => {
  const value = Number(localStorage.getItem(storageKey()))
  return Number.isSafeInteger(value) && value > 0 ? value : undefined
}

const persistMembership = (data: CurrentEducationWorkspaceVO) => {
  const key = storageKey(data.userId, data.tenantId)
  const isLegalMembership =
    data.membershipId !== undefined &&
    data.organizations.some((item) => item.membershipId === data.membershipId)
  if (isLegalMembership) localStorage.setItem(key, String(data.membershipId))
  else localStorage.removeItem(key)
}

const applyWorkspace = (data: CurrentEducationWorkspaceVO) => {
  workspace.value = data
  setEducationMembershipId(data.membershipId)
  persistMembership(data)
  status.value = 'ready'
  error.value = undefined
  return data
}

const loadWorkspace = async (membershipId?: number) => {
  status.value = 'loading'
  error.value = undefined
  try {
    return applyWorkspace(await getCurrentEducationWorkspace(membershipId))
  } catch (reason) {
    status.value = 'error'
    error.value = reason
    throw reason
  }
}

const initialize = (force = false) => {
  const currentUserId = getCurrentUserId()
  const currentTenantId = Number(getTenantId()) || 0
  const matchesCurrentIdentity =
    workspace.value?.userId === currentUserId && workspace.value?.tenantId === currentTenantId

  if (!force && matchesCurrentIdentity && workspace.value) return Promise.resolve(workspace.value)
  if (!force && pendingInitialization) return pendingInitialization

  pendingInitialization = loadWorkspace(readStoredMembershipId()).finally(() => {
    pendingInitialization = undefined
  })
  return pendingInitialization
}

const switchOrganization = async (membershipId?: number) => {
  if (!workspace.value) throw new Error('教育工作台尚未初始化')
  const target = workspace.value.organizations.find((item) => item.membershipId === membershipId)
  if (!target) throw new Error('只能切换到当前账号已授权的教育组织')
  if (workspace.value.membershipId === target.membershipId) return workspace.value
  return loadWorkspace(target.membershipId)
}

const retry = () => initialize(true)

const reset = () => {
  workspace.value = undefined
  setEducationMembershipId(undefined)
  status.value = 'idle'
  error.value = undefined
  pendingInitialization = undefined
}

export const useEducationWorkspace = () => ({
  workspace: readonly(workspace),
  status: readonly(status),
  error: readonly(error),
  loading: computed(() => status.value === 'loading'),
  ready: computed(() => status.value === 'ready'),
  membershipId: computed(() => workspace.value?.membershipId),
  organizations: computed<EducationOrganizationOptionVO[]>(
    () => workspace.value?.organizations || []
  ),
  initialize,
  switchOrganization,
  retry,
  reset
})
